import React, { useEffect, useRef } from "react";

const AboutSection = () => {
  const sectionRef = useRef(null);
  const badgeRef = useRef(null);
  const headlineRef = useRef(null);
  const storyRef = useRef(null);
  const statsRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-reveal");
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = [
      badgeRef.current,
      headlineRef.current,
      storyRef.current,
      statsRef.current,
      ctaRef.current
    ].filter(Boolean);

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative bg-white overflow-hidden">
      
      {/* ===== LIGHT, AIRY BACKGROUND - MATCHES HERO ===== */}
      <div className="absolute inset-0">
        {/* Soft teal gradient - from hero section */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-teal-50/40 via-transparent to-transparent rounded-full blur-3xl animate-float"></div>
        
        {/* Warm amber glow - from campaigns section */}
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-gradient-to-tr from-amber-50/40 via-transparent to-transparent rounded-full blur-3xl animate-float-delay"></div>
        
        {/* Soft emerald center - cohesive palette */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-r from-emerald-50/30 via-teal-50/30 to-emerald-50/30 rounded-full blur-3xl animate-pulse-slow"></div>
        
        {/* Delicate dot pattern - from features section */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, #14b8a6 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}></div>
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 lg:py-28">
        
        {/* ===== DECORATIVE TOP WAVE - ADDS VISUAL INTEREST ===== */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-24">
          <div className="w-full h-full bg-gradient-to-b from-teal-100/50 to-transparent rounded-full blur-2xl"></div>
        </div>

        {/* ===== ORIGIN BADGE - LIGHT TEAL ===== */}
        <div 
          ref={badgeRef}
          className="opacity-0 -translate-y-4 mb-10 inline-flex items-center gap-3 px-5 py-2.5 bg-white/80 backdrop-blur-sm rounded-full border border-teal-100 shadow-sm hover:shadow-md transition-all duration-300"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-teal-500"></span>
          </span>
          <span className="text-xs font-medium text-teal-700 uppercase tracking-[0.2em]">
            Since 2014
          </span>
        </div>

        {/* ===== HEADLINE - GRADIENT TEXT WITH DECORATION ===== */}
        <div className="relative mb-12">
          <h2 
            ref={headlineRef}
            className="opacity-0 translate-y-8 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight"
          >
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">
                A vision
              </span>
              <span className="absolute -bottom-2 left-0 w-full h-3 bg-gradient-to-r from-teal-200/50 to-emerald-200/50 blur-sm"></span>
            </span>
            <br />
            <span className="relative inline-block mt-2">
              <span className="relative z-10 text-gray-800">
                to reimagine giving.
              </span>
              <span className="absolute -bottom-2 left-0 w-full h-0.5 bg-gradient-to-r from-teal-400 to-emerald-400"></span>
            </span>
          </h2>
          
          {/* Decorative floating elements - fills empty space */}
          <div className="absolute -right-12 top-0 hidden lg:block">
            <div className="relative">
              <div className="w-20 h-20 bg-teal-100/30 rounded-full blur-xl animate-float"></div>
              <div className="absolute -top-6 -right-6 w-12 h-12 bg-amber-100/30 rounded-full blur-lg animate-float-delay"></div>
            </div>
          </div>
        </div>

        {/* ===== STORY CARD - LIGHT, AIRY, FULL OF DETAIL ===== */}
        <div 
          ref={storyRef}
          className="opacity-0 translate-y-8 mb-20"
        >
          <div className="relative group">
            {/* Soft glow on hover */}
            <div className="absolute -inset-2 bg-gradient-to-r from-teal-100/50 to-emerald-100/50 rounded-3xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-700"></div>
            
            <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-10 md:p-12 border border-white/60 shadow-xl shadow-teal-100/30">
              
              {/* Decorative corner accents - soft */}
              <div className="absolute top-6 left-6 w-16 h-16 border-l-2 border-t-2 border-teal-200/70 rounded-tl-3xl"></div>
              <div className="absolute bottom-6 right-6 w-16 h-16 border-r-2 border-b-2 border-emerald-200/70 rounded-br-3xl"></div>
              
              {/* Floating paper planes - adds movement, fills space */}
              <div className="absolute -top-8 -right-8 text-4xl opacity-20 group-hover:opacity-40 transition-opacity duration-700 animate-float-slow">
                ✈️
              </div>
              <div className="absolute -bottom-8 -left-8 text-4xl opacity-20 group-hover:opacity-40 transition-opacity duration-700 animate-float-delay">
                ✨
              </div>
              
              {/* Opening quote - soft teal */}
              <span className="text-7xl font-serif text-teal-200/70 leading-none absolute -top-3 left-8">"</span>
              
              {/* Story content - with rich formatting */}
              <div className="space-y-6 text-gray-700 text-lg md:text-xl leading-relaxed relative z-10 px-4 md:px-8">
                <p className="font-medium text-teal-700 text-xl md:text-2xl">
                  DonatePe was born from a vision to reimagine charitable giving.
                </p>
                
                <div className="flex items-start gap-4">
                  <div className="w-1 h-12 bg-gradient-to-b from-teal-400 to-emerald-400 rounded-full mt-1"></div>
                  <p className="text-gray-600">
                    As crowdfunding began revolutionizing how people supported material needs,{' '}
                    <span className="font-semibold text-teal-600">we saw an opportunity to create something more</span>—
                    a platform where hope and spiritual renewal could flourish alongside tangible aid.
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-teal-50/50 to-emerald-50/50 p-6 rounded-2xl border border-teal-100/50">
                  <p className="italic text-gray-700">
                    "After navigating challenges and refining our approach, we launched our platform
                    ready to connect compassionate donors with causes that matter."
                  </p>
                </div>
              </div>
              
              {/* Closing quote - soft emerald */}
              <span className="absolute bottom-2 right-10 text-7xl font-serif text-emerald-200/70 leading-none rotate-180">"</span>
              
              {/* Signature line */}
              <div className="absolute bottom-8 left-12 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                  DP
                </div>
                <span className="text-xs text-gray-400">— The DonatePe team</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== STATS - LIGHT, MODERN, FULL OF DETAIL ===== */}
        <div 
          ref={statsRef}
          className="opacity-0 translate-y-8 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-20"
        >
          {[
            { 
              label: 'Campaigns', 
              value: '50,000+', 
              icon: '🚀', 
              color: 'from-teal-50 to-teal-100/50',
              border: 'border-teal-200',
              text: 'text-teal-700',
              description: 'and counting'
            },
            { 
              label: 'Donors', 
              value: '2M+', 
              icon: '❤️', 
              color: 'from-amber-50 to-amber-100/50',
              border: 'border-amber-200',
              text: 'text-amber-700',
              description: 'worldwide'
            },
            { 
              label: 'Raised', 
              value: '₹200Cr+', 
              icon: '✨', 
              color: 'from-emerald-50 to-emerald-100/50',
              border: 'border-emerald-200',
              text: 'text-emerald-700',
              description: 'and growing'
            }
          ].map((stat, index) => (
            <div 
              key={index}
              className="group relative"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Decorative background layer */}
              <div className={`absolute -inset-3 bg-gradient-to-br ${stat.color} rounded-3xl blur-2xl opacity-0 group-hover:opacity-70 transition-opacity duration-700`}></div>
              
              <div className={`relative bg-white/90 backdrop-blur-sm rounded-2xl p-8 text-center border ${stat.border} shadow-lg hover:shadow-xl transition-all duration-500 group-hover:-translate-y-2`}>
                
                {/* Icon with gradient circle */}
                <div className="relative inline-block mb-4">
                  <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} rounded-full blur-md opacity-70`}></div>
                  <div className={`relative w-16 h-16 mx-auto bg-gradient-to-br ${stat.color} rounded-2xl flex items-center justify-center text-3xl shadow-md group-hover:scale-110 transition-transform duration-500`}>
                    {stat.icon}
                  </div>
                </div>
                
                {/* Stats */}
                <p className={`text-3xl md:text-4xl font-bold ${stat.text} mb-1`}>{stat.value}</p>
                <p className="text-sm font-medium text-gray-700 uppercase tracking-wider mb-1">{stat.label}</p>
                <p className="text-xs text-gray-400">{stat.description}</p>
                
                {/* Decorative dot */}
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gray-300 group-hover:w-8 group-hover:bg-gradient-to-r group-hover:from-teal-400 group-hover:to-emerald-400 transition-all duration-500"></div>
              </div>
            </div>
          ))}
        </div>

        {/* ===== CTA - LIGHT, INVITING, FULL OF DETAIL ===== */}
        <div 
          ref={ctaRef}
          className="opacity-0 translate-y-8 flex flex-col items-center"
        >
          {/* Decorative rings */}
          <div className="relative mb-8">
            <div className="absolute -inset-4 bg-gradient-to-r from-teal-100/50 to-emerald-100/50 rounded-full blur-2xl"></div>
            <div className="absolute -inset-2 bg-white/80 rounded-full blur-sm"></div>
            
            <a 
              href="/about" 
              className="relative flex items-center gap-4 px-12 py-5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl hover:shadow-teal-200/50 transition-all duration-500 hover:scale-105 group"
            >
              <span className="relative z-10 flex items-center gap-3">
                <span>Read our full story</span>
                <svg className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              
              {/* Shine effect */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </a>
          </div>
          
          {/* Rich footer with multiple elements - fills empty space */}
          <div className="flex flex-col items-center gap-6">
            
            {/* Timeline decoration */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-teal-300 to-transparent"></div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-teal-600 bg-teal-50 px-3 py-1.5 rounded-full">2014</span>
                <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full">2024</span>
              </div>
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-emerald-300 to-transparent"></div>
            </div>
            
            {/* Community avatars - adds life */}
            <div className="flex items-center gap-4">
              <div className="flex -space-x-3">
                {[1,2,3,4].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-400 to-emerald-400 border-2 border-white shadow-md flex items-center justify-center text-white text-xs font-bold">
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
                <div className="w-8 h-8 rounded-full bg-white border-2 border-teal-200 shadow-md flex items-center justify-center text-xs font-medium text-teal-600">
                  +2k
                </div>
              </div>
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">50,000+</span> changemakers
              </p>
            </div>
            
            {/* Trust message */}
            <p className="text-xs text-gray-400 flex items-center gap-2">
              <span className="w-1 h-1 bg-teal-400 rounded-full animate-pulse"></span>
              <span className="italic">From a vision to a movement — 10 years of impact</span>
              <span className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse"></span>
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes reveal {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-reveal {
          animation: reveal 0.9s cubic-bezier(0.23, 1, 0.32, 1) forwards;
        }
        
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
            opacity: 0.4;
          }
          33% { 
            transform: translate(20px, -15px) scale(1.05); 
            opacity: 0.5;
          }
          66% { 
            transform: translate(-15px, 10px) scale(0.95); 
            opacity: 0.45;
          }
        }
        
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
        
        .animate-float-delay {
          animation: float 25s ease-in-out infinite reverse;
        }
        
        .animate-float-slow {
          animation: float 30s ease-in-out infinite;
        }
        
        @keyframes pulse-slow {
          0%, 100% { 
            opacity: 0.2; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.3; 
            transform: scale(1.05); 
          }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default AboutSection;